import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ViewDiffButton } from "src/app/features/TerminalVersion/components/buttons/ViewDiffButton";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayDateTimeValue,
    displayEnumValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import type { TerminalVersionList } from "../../types/TerminalVersionList";

export const TerminalVersionsTable: FC<ResourceTableProps<TerminalVersionList>> = ({
    datasource,
    resource,
    onRowSelectionChange,
    onStateChanged,
    selectedRowKeys,
}) => {
    const { t } = useTranslation();

    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<TerminalVersionList>): ExtendedColumnsType<TerminalVersionList>[] => {
            return [
                {
                    title: t("terminal.id"),
                    dataIndex: "id",
                    key: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("terminal.terminalId"),
                    dataIndex: "terminalId",
                    key: "terminalId",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "terminalId" ? sortInfo.order : null,
                },
                {
                    title: t("terminal.organizationUnit.name"),
                    dataIndex: "siteId",
                    key: "siteId",
                    sorter: () => 0,
                    sortField: "organizationUnit.name",
                    sortOrder: sortInfo.columnKey === "siteId" ? sortInfo.order : null,
                    render: (value) => displayEnumValue(value, "site-name"),
                },
                {
                    title: t("terminal.appVersion"),
                    dataIndex: "appVersion",
                    key: "appVersion",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "appVersion" ? sortInfo.order : null,
                },
                {
                    title: t("terminal.confVersion"),
                    dataIndex: "confVersion",
                    key: "confVersion",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "confVersion" ? sortInfo.order : null,
                },
                {
                    title: t("terminal.lastChangeVersion"),
                    dataIndex: "lastChangeVersion",
                    key: "lastChangeVersion",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "lastChangeVersion" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
                {
                    title: t("terminalVersions.diff.header"),
                    dataIndex: "diffAvailable",
                    key: "diffAvailable",
                    render: (value, record) => value && <ViewDiffButton item={record} />,
                },
            ];
        },
        [t],
    );

    const defineBackGroundColour = (record: TerminalVersionList) => {
        if (record.affected) return "aevi-color-secondary aevi-background";
        if (record.updated) return "aevi-color-primary aevi-background";
        return "aevi-background";
    };

    return (
        (datasource && (
            <GenericTable<TerminalVersionList>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                name="terminalVersions_table"
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                rowClassName={(record: TerminalVersionList) => defineBackGroundColour(record)}
                resource={resource}
                selectedRowKeys={selectedRowKeys}
            />
        )) ||
        null
    );
};
